import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import { Input } from "@common/components/ui/input"
import type { ComponentPropsWithoutRef } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"

type BareProps<T extends FieldValues> = Omit<
  ComponentPropsWithoutRef<typeof Input>,
  "name" | "value" | "defaultValue" | "onChange" | "onBlur"
> & {
  name: Path<T>
  optional?: boolean
}

export function BareTextField<T extends FieldValues>({
  name,
  optional,
  type = "text",
  ...rest
}: BareProps<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        renderTextInput<T>({
          field,
          invalid: !!fieldState.error,
          optional,
          type,
          ...rest,
        })
      }
    />
  )
}

export function renderTextInput<T extends FieldValues>({
  field,
  invalid,
  optional,
  type = "text",
  ...rest
}: Omit<BareProps<T>, "name"> & {
  field: ControllerRenderProps<T, Path<T>>
  invalid: boolean
}) {
  return (
    <Input
      type={type}
      aria-required={!optional}
      aria-invalid={invalid ? true : undefined}
      {...rest}
      {...field}
      value={(field.value ?? "") as string}
    />
  )
}

type Props<T extends FieldValues> = BareProps<T> & {
  label?: string
  inputClassName?: string
}

export function TextField<T extends FieldValues>({
  name,
  label,
  className,
  inputClassName,
  optional,
  ...rest
}: Props<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          {label !== undefined && (
            <FieldLabel label={label} optional={optional} />
          )}
          <FormControl>
            {renderTextInput<T>({
              field,
              invalid: !!fieldState.error,
              optional,
              className: inputClassName,
              ...rest,
            })}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
